import { Autocomplete, Callout, FormField } from '@dev-spendesk/grapes';
import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { useAccountingSoftwareName } from 'src/core/modules/accounting-integration/apis';

import { NoOptions } from './NoOptions';
import { type AnalyticalFieldValueMappingV2 } from '../../../bookkeep/settings/accounting';
import { type SyncMode } from '../../apis/useSetSyncSettings';

type Props = {
  values: AnalyticalFieldValueMappingV2[];
  onValueSelected: (value: AnalyticalFieldValueMappingV2 | undefined) => void;
  valueInDatabase?: Option;
  selectedValue?: Option;
  syncMode?: SyncMode;
};

type Option = AnalyticalFieldValueMappingV2 & {
  key: string;
  label: string;
};

const mapOption = (value: AnalyticalFieldValueMappingV2): Option => ({
  ...value,
  key: value.id,
  label: value.externalName,
});

export const AnalyticalFieldValueMappingSelector = ({
  values,
  onValueSelected,
  selectedValue,
  syncMode,
  valueInDatabase,
}: Props) => {
  const { t } = useTranslation('global');
  const { accountingSoftwareName } = useAccountingSoftwareName();
  const [options, setOptions] = useState<Option[]>([]);

  const notMappedValues = useMemo(() => {
    const notMapped = values.filter((value) => !value.internalId);
    if (
      valueInDatabase &&
      !notMapped.some((v) => v.id === valueInDatabase.id)
    ) {
      notMapped.push(valueInDatabase);
    }
    return notMapped;
  }, [values, valueInDatabase]);

  useEffect(() => {
    setOptions(notMappedValues.map(mapOption));
  }, [values, valueInDatabase]);

  const handleSearch = (value: string | undefined) => {
    if (!value) {
      setOptions(values.map(mapOption));
      return;
    }

    setOptions((previous) =>
      previous.filter(({ label }) =>
        label.toLowerCase().includes(value.toLowerCase()),
      ),
    );
  };

  const handleChange = (option: Option | undefined) => {
    onValueSelected(option);
  };

  return (
    <div className="mt-24">
      <FormField
        label={t(
          'bookkeep.integrations.settings.analyticalFieldMapping.valueMappingSelector.label',
          { integration: accountingSoftwareName },
        )}
        hint={t('misc.optional')}
        infoTipContent={
          syncMode === 'pull' &&
          t(
            'bookkeep.integrations.settings.analyticalFieldMapping.disableDeleteTooltip',
            { integration: accountingSoftwareName },
          )
        }
      >
        <Autocomplete<Option>
          fit="parent"
          options={options}
          showClearSelectionButton
          value={selectedValue}
          onSearch={handleSearch}
          onSelect={handleChange}
          placeholder={t(
            'bookkeep.integrations.settings.analyticalFieldMapping.configurationPage.autoCompletePlaceholder',
          )}
          isDisabled={syncMode === 'pull'}
          renderNoOptions={() => <NoOptions />}
        />
      </FormField>
      {!selectedValue && (
        <Callout
          className="mt-8 bg-transparent"
          variant="info"
          title={t(
            'bookkeep.integrations.settings.analyticalFieldMapping.valueMappingSelector.callout',
            { integration: accountingSoftwareName },
          )}
        />
      )}
    </div>
  );
};
